import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../../context/LanguageContext";
import { Button } from "../ui/button";
import { ArrowRight, Check, Target, Truck as TruckIcon, Users, Plus, Equal } from "lucide-react";

const BANNER_IMG =
  "https://customer-assets.emergentagent.com/job_lklog-partner/artifacts/ekwgv9f6_LKLOG6lat.jpg";

export default function Hero() {
  const { t } = useLang();
  const usps = [t("hero.usp1"), t("hero.usp2"), t("hero.usp3"), t("hero.usp4")];

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden"
    >
      {/* Background: 6-year banner */}
      <div className="absolute inset-0">
        <img
          src={BANNER_IMG}
          alt="Dziękujemy za 6 lat współpracy — LK LOG"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/75 to-[#0A0A0A]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-36 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="gold-divider" />
            <span className="label-gold">{t("anniversary.label")} · {t("hero.eyebrow")}</span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-display text-white mb-6 leading-[0.95] tracking-tight"
          >
            <span className="block text-2xl sm:text-3xl lg:text-4xl font-medium text-neutral-200 mb-2">
              {t("anniversary.title_pre")}
            </span>
            <span className="block text-7xl sm:text-8xl lg:text-[9rem] font-bold text-[#F2B705] leading-none my-1">
              {t("anniversary.title_big")}
            </span>
            <span className="block text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mt-2">
              {t("anniversary.title_post")}
            </span>
          </h1>

          <p className="text-xs sm:text-sm tracking-[0.24em] uppercase text-[#F2B705]/90 mb-6">
            {t("hero.tagline")}
          </p>

          <p className="text-base sm:text-lg text-neutral-300 max-w-2xl leading-relaxed mb-10">
            {t("hero.subtitle")}
          </p>

          {/* Brand equation — single line */}
          <div className="mb-12 -mx-4 sm:mx-0 overflow-x-auto sm:overflow-visible" data-testid="hero-equation">
            <div className="flex items-center gap-2 md:gap-3 px-4 sm:px-0 sm:flex-wrap lg:flex-nowrap">
              <div className="flex items-center gap-2 bg-[#0A0A0A]/70 border border-[#F2B705]/25 rounded-sm px-3 py-2.5 backdrop-blur-sm whitespace-nowrap shrink-0">
                <Target className="w-3.5 h-3.5 text-[#F2B705] shrink-0" strokeWidth={1.5} />
                <span className="text-[11px] md:text-xs lg:text-sm font-semibold tracking-wide uppercase text-white">
                  {t("anniversary.line1")}
                </span>
              </div>
              <Plus className="w-3.5 h-3.5 text-[#F2B705] shrink-0" strokeWidth={2} />
              <div className="flex items-center gap-2 bg-[#0A0A0A]/70 border border-[#F2B705]/25 rounded-sm px-3 py-2.5 backdrop-blur-sm whitespace-nowrap shrink-0">
                <TruckIcon className="w-3.5 h-3.5 text-[#F2B705] shrink-0" strokeWidth={1.5} />
                <span className="text-[11px] md:text-xs lg:text-sm font-semibold tracking-wide uppercase text-white">
                  {t("anniversary.line2")}
                </span>
              </div>
              <Equal className="w-3.5 h-3.5 text-[#F2B705] shrink-0" strokeWidth={2} />
              <div className="flex items-center gap-2 bg-[#F2B705]/15 border border-[#F2B705]/60 rounded-sm px-3 py-2.5 backdrop-blur-sm whitespace-nowrap shrink-0">
                <Users className="w-3.5 h-3.5 text-[#F2B705] shrink-0" strokeWidth={1.5} />
                <span className="text-[11px] md:text-xs lg:text-sm font-semibold tracking-wide uppercase text-[#F2B705]">
                  {t("anniversary.line3")}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <Link to="/kontakt" data-testid="hero-cta-primary">
              <Button
                size="lg"
                className="bg-[#F2B705] hover:bg-[#FFC933] text-[#0A0A0A] font-semibold rounded-sm px-8 py-6 text-base group"
              >
                {t("hero.cta1")}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/o-nas" data-testid="hero-cta-secondary">
              <Button
                size="lg"
                variant="outline"
                className="border-[#F2B705]/50 text-[#F2B705] hover:bg-[#F2B705]/10 hover:text-[#F2B705] rounded-sm px-8 py-6 text-base bg-transparent"
              >
                {t("hero.cta2")}
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3 max-w-3xl">
            {usps.map((u, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.08 }}
                className="flex items-start gap-3 text-sm text-neutral-200"
              >
                <Check className="w-4 h-4 text-[#F2B705] mt-0.5 shrink-0" strokeWidth={2.5} />
                <span>{u}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
